import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { LanguageModule, SharedLanguageService } from '@language';
import {
    ActionItem,
    ApiFragment,
    getShareContext,
    InlineDescriptionListItem,
    NavigationItem,
    NotificationType,
    PartialRecord,
    QueryIteratorStack,
    QueryIteratorStackEntry,
    RecordStatus,
    RecordType,
    RecordTypeStructure,
    RecordTypeTranslationService,
    ShNotificationComponent,
    ShPageHeaderComponent,
    ShPluginTabsService,
    TabItem
} from '@stylehaven/components';
import isEqual from 'lodash-es/isEqual';
import { NotificationItem } from '../../../common/entities/common/notification-item.entity';
import { HasKeyFramesGuard } from '../../guards/has-keyframes-guard';

@Component({
    selector: 'med-record-header',
    templateUrl: './record-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShPageHeaderComponent, ShNotificationComponent, LanguageModule, RouterModule]
})
export class RecordHeaderContainerComponent implements OnInit, OnDestroy {
    private readonly inputRecordActionItems$: ReplaySubject<ActionItem[]> = new ReplaySubject<ActionItem[]>(1);
    private readonly tabBlackList: Record<string, RecordTypeStructure[]> = {
        details: [],
        edit: [RecordTypeStructure.Representation],
        items: [RecordTypeStructure.DataFlat, RecordTypeStructure.Representation],
        keyframes: [RecordTypeStructure.Intellectual, RecordTypeStructure.Representation],
        representations: [
            RecordTypeStructure.DataFlat,
            RecordTypeStructure.Representation,
            RecordTypeStructure.Intellectual
        ],
        relations: [RecordTypeStructure.Representation],
        hierarchy: [RecordTypeStructure.Intellectual, RecordTypeStructure.Representation],
        exif: [RecordTypeStructure.Intellectual]
    };

    public readonly NotificationType = NotificationType;

    public readonly record$: ReplaySubject<ApiFragment> = new ReplaySubject<ApiFragment>(1);
    public readonly recordParent$: BehaviorSubject<ApiFragment | null> = new BehaviorSubject<ApiFragment | null>(null);
    public readonly mainFragment$: BehaviorSubject<ApiFragment | null> = new BehaviorSubject<ApiFragment | null>(null);
    public readonly editMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public showIncompleteRepresentationNotification$: Observable<boolean>;
    public showTransferredNotification$: Observable<boolean>;
    public recordTitle$: Observable<string>;
    public recordContextItems$: Observable<InlineDescriptionListItem[]>;
    public recordActionItems$: Observable<ActionItem[]>;
    public recordTabItems$: Observable<TabItem[]>;

    @Input() public primaryNavigationItems: NavigationItem[];
    @Input() public secondaryNavigationItems: NavigationItem[];
    @Input() public tabNavigationItems: ActionItem[];
    @Input() public notification?: NotificationItem;
    @Input() set editMode(editMode: boolean) {
        this.editMode$.next(editMode);
    }

    @Input() set record(record: ApiFragment) {
        this.record$.next(record);
    }

    @Input() set recordParent(recordParent: ApiFragment) {
        this.recordParent$.next(recordParent);
    }

    @Input() set mainFragment(mainFragment: ApiFragment) {
        this.mainFragment$.next(mainFragment);
    }

    @Input() set actionItems(actionItems: ActionItem[]) {
        if (actionItems) {
            this.inputRecordActionItems$.next(actionItems);
        }
    }

    @Input() private showExtraTabs = false;
    @Input() breadcrumbStack: QueryIteratorStack;

    private readonly recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);
    private readonly pluginTabsService: ShPluginTabsService = inject(ShPluginTabsService);

    constructor(
        private readonly translateService: SharedLanguageService,
        private readonly router: Router,
        private readonly keyFramesGuard: HasKeyFramesGuard
    ) {}

    ngOnInit(): void {
        this.initRecordStreams();
        this.initNotifcationStream();
        this.initTabItems();
    }

    private initRecordStreams(): void {
        this.recordTitle$ = this.initRecordTitleStream();
        this.recordContextItems$ = this.initRecordContextItemsStream();
        this.recordActionItems$ = this.initRecordActionItemsStream();
    }

    private initRecordTitleStream(): Observable<string> {
        return this.record$.pipe(
            map((fragment: PartialRecord) => fragment.getTitle()),
            distinctUntilChanged(isEqual)
        );
    }

    private initRecordActionItemsStream(): Observable<ActionItem[]> {
        return this.inputRecordActionItems$.pipe(distinctUntilChanged(isEqual));
    }

    private initRecordContextItemsStream(): Observable<InlineDescriptionListItem[]> {
        return combineLatest([this.record$, this.recordParent$, this.mainFragment$, getShareContext(this.route)]).pipe(
            switchMap(
                ([fragment, fragmentParent, mainFragment, shareContext]: [
                    ApiFragment,
                    ApiFragment | null,
                    ApiFragment | null,
                    string | undefined
                ]) => this.mapRecordToContextItems(fragment, fragmentParent, mainFragment, shareContext)
            ),
            distinctUntilChanged(isEqual)
        );
    }

    private mapRecordToContextItems(
        fragment: ApiFragment,
        fragmentParent: ApiFragment | null,
        mainFragment: ApiFragment | null,
        shareContext: string | undefined
    ): Observable<InlineDescriptionListItem[]> {
        let classificationTitle: string | null;
        if (fragment.isCandidateChild()) {
            if (fragmentParent) {
                classificationTitle = fragmentParent.getClassificationTitle();
            }
        } else {
            classificationTitle = fragment.getClassificationTitle();
        }

        const parentContextItem$ = this.getParentContextItem(fragment, fragmentParent, shareContext).pipe(
            map(item => item ?? null)
        );
        const mainFragmentContextItem$ = this.getMainFragmentContextItem(fragment, mainFragment, shareContext);

        return combineLatest([parentContextItem$, mainFragmentContextItem$]).pipe(
            map(([parentItem, mainFragmentItem]) =>
                [
                    this.getClassificationContextItem(classificationTitle),
                    parentItem,
                    mainFragmentItem,
                    this.getTypeContextItem(fragment),
                    this.getRecordStatusContextItem(fragment)
                ].filter((item): item is InlineDescriptionListItem => !!item)
            )
        );
    }

    private getClassificationContextItem(classificationTitle: string | null): InlineDescriptionListItem {
        return {
            term: this.translateService.instant('fragments.classification'),
            details: classificationTitle ?? this.translateService.instant('classifications.errors.title-unavailable'),
            icon: {
                name: 'file-text',
                iconOnly: true
            }
        };
    }

    private getParentContextItem(
        fragment: ApiFragment,
        fragmentParent: ApiFragment | null,
        shareContext: string | undefined
    ): Observable<InlineDescriptionListItem | null> {
        const mainPath: string = shareContext ? `shared/${shareContext}/single` : 'records';
        if (fragment.isCandidateChild()) {
            return this.recordParent$.pipe(
                map(parentRecord => {
                    const directParentTitle = parentRecord?.getTitle() ?? null;
                    const route = `/${mainPath}/${fragment.getCandidateParentId()}`;
                    return this.handleParentHeaderLogic(
                        directParentTitle,
                        parentRecord,
                        route,
                        undefined,
                        shareContext
                    );
                })
            );
        } else if (fragment.hasParent()) {
            const directParentTitle = fragmentParent?.getTitle() ?? fragment.getDirectParentTitle();
            const route = `/${mainPath}/${fragment.getParentRecordId()}`;

            return of(this.handleParentHeaderLogic(directParentTitle, fragmentParent, route, undefined, shareContext));
        } else {
            return of(null);
        }
    }

    private getMainFragmentContextItem(
        fragment: ApiFragment,
        mainFragment: ApiFragment | null,
        shareContext: string | undefined = undefined
    ): Observable<InlineDescriptionListItem | null> {
        if (mainFragment) {
            const mainPath: string = shareContext ? `shared/${shareContext}/single` : 'records';
            return this.mainFragment$.pipe(
                map(mainFragment => {
                    const directParentTitle = mainFragment?.getTitle() ?? null;
                    const route = `/${mainPath}/${mainFragment?.getFragmentId()}`;
                    return this.handleParentHeaderLogic(
                        directParentTitle,
                        mainFragment,
                        route,
                        fragment.isPureFragment(),
                        shareContext
                    );
                })
            );
        } else {
            return of(null);
        }
    }

    private handleParentHeaderLogic(
        directParentTitle: string | null,
        fragmentParent: ApiFragment | null,
        route: string,
        isFragment: boolean = false,
        shareContext: string | undefined = undefined
    ): InlineDescriptionListItem | null {
        const baseItem: InlineDescriptionListItem = {
            term: `${this.translateService.instant(isFragment ? 'fragments.fragment-of' : 'fragments.part-of')}:`,
            details: directParentTitle ?? fragmentParent?.getTitle() ?? '',
            icon: {
                name: 'image-stack',
                iconOnly: false
            }
        };

        if (!fragmentParent) {
            return {
                ...baseItem,
                navigationLink: {
                    route: route,
                    extras: {
                        state: {
                            queryIteratorStack: this.getParentBreadCrumbStack(route, this.breadcrumbStack ?? [])
                        }
                    }
                }
            };
        } else {
            const fragmentUrl: string = shareContext
                ? fragmentParent.getShareDefaultUrl(shareContext)
                : (fragmentParent.getDefaultUrl() ?? '');
            return {
                ...baseItem,
                navigationLink: {
                    route: fragmentUrl,
                    extras: {
                        state: {
                            queryIteratorStack: this.getParentBreadCrumbStack(fragmentUrl, this.breadcrumbStack ?? [])
                        }
                    }
                }
            };
        }
    }

    private getTypeContextItem(record: ApiFragment): InlineDescriptionListItem {
        return {
            term: `${this.translateService.instant('fragments.type')}`,
            details: this.translateType(record),
            icon: {
                name: record.getTypeIconName(),
                iconOnly: true
            }
        };
    }

    private getRecordStatusContextItem(record: ApiFragment): InlineDescriptionListItem {
        return {
            term: `${this.translateService.instant('fragments.record-status')}`,
            details: this.translateRecordStatus(record.getRecordStatus()),
            icon: {
                name: 'flag',
                iconOnly: true
            }
        };
    }

    private translateType(record: ApiFragment): string {
        return this.recordTypeTranslationService.getRecordTypeTranslation(
            record.getRecordType(),
            record.getMediaHavenType()
        );
    }

    private translateRecordStatus(status: RecordStatus): string {
        return this.translateService.instant(`overrides.RecordStatus.${status}`);
    }

    private initNotifcationStream(): void {
        this.showIncompleteRepresentationNotification$ = this.record$.pipe(
            map(record => record.getRecordType() === RecordType.Record && record.hasIncompleteBrowse())
        );

        this.showTransferredNotification$ = this.record$.pipe(
            map(
                record =>
                    record.getRecordStatus() === RecordStatus.Transferred &&
                    !!record.metadata.Dynamic.DossierDefinitiveTransferDate
            )
        );
    }

    private initTabItems(): void {
        const defaultTabItems$: Observable<TabItem[]> = combineLatest([this.record$, this.editMode$]).pipe(
            map(([record, editMode]) => this.getTabItems(record, editMode))
        );

        // Only show when extra tabs are allowed
        const pluginTabItems$: Observable<TabItem[]> = this.showExtraTabs
            ? this.record$.pipe(switchMap(record => this.pluginTabsService.getPluginTabs(record)))
            : of([]);

        // Plugin tabs could take a while to load. This ensures that the default tabs are always loaded first
        // Plugin tabs can appear delayed once ready.
        const allTabItems$: Observable<TabItem[]> = combineLatest([defaultTabItems$, pluginTabItems$]).pipe(
            map(([defaultTabs, pluginTabs]) => [...defaultTabs, ...pluginTabs])
        );

        this.recordTabItems$ = defaultTabItems$;

        this.recordTabItems$ = merge(defaultTabItems$, allTabItems$);
    }

    private getTabItems(record: ApiFragment, editMode: boolean): TabItem[] {
        const recordTypeStructure: RecordTypeStructure = record.getRecordStructure();

        return [
            {
                route: editMode ? ['edit'] : ['details'],
                label: this.translateService.instant('core.metadata')
            },
            this.getShowItemsCondition(record)
                ? {
                      route: ['items'],
                      label: this.translateService.instant('navigation.contents')
                  }
                : null,
            this.keyFramesGuard.hasAccess(record)
                ? {
                      route: ['keyframes'],
                      label: this.translateService.instant('keyframes.type.contents')
                  }
                : null,
            {
                route: ['representations'],
                label: this.translateService.translate('core.representations')
            },
            {
                route: ['relations'],
                label: this.translateService.instant('core.relations')
            },
            {
                route: ['hierarchy'],
                label: this.translateService.instant('core.hierarchy')
            },
            {
                route: ['exif'],
                label: this.translateService.instant('core.exif')
            },
            {
                route: this.showExtraTabs ? ['history'] : [''],
                label: this.showExtraTabs ? this.translateService.instant('core.history') : ''
            }
        ].filter(
            tabItem =>
                !!tabItem &&
                (!this.tabBlackList.hasOwnProperty(tabItem.route[0]) ||
                    !this.tabBlackList[tabItem.route[0]].find(structure => recordTypeStructure === structure))
        ) as TabItem[];
    }

    private getShowItemsCondition(record: ApiFragment): boolean {
        return (
            (record.hasFragments() && record.getRecordStructure() === RecordTypeStructure.Data) ||
            record.hasNonRepresentationChildren() ||
            (record.getRecordStructure() === RecordTypeStructure.Intellectual && record.canHaveChildren())
        );
    }

    shouldEnableNavigationItem(route: string[]): Observable<boolean> {
        return this.editMode$.pipe(map(editMode => this.isNavigationItemEnabled(route, editMode)));
    }

    private isNavigationItemEnabled(route: string[], editMode: boolean): boolean {
        if (!editMode) {
            return true;
        }

        if (this.router.url.includes('edit')) {
            const currentRoute: string = this.router.createUrlTree(route).toString();

            if (this.router.url.includes(currentRoute)) {
                return true;
            }
        }

        return false;
    }

    private getParentBreadCrumbStack(parentRoute: string, stack: QueryIteratorStack): QueryIteratorStack {
        if (stack.length > 0) {
            const [last]: QueryIteratorStackEntry[] = stack.slice(-1);
            if (last.pageUrl === parentRoute) {
                return stack.slice(0, -1);
            }
        }
        return stack;
    }

    ngOnDestroy(): void {
        this.record$.complete();
        this.recordParent$.complete();
        this.inputRecordActionItems$.complete();
        this.editMode$.complete();
    }
}
